import {
  EyeInvisibleOutlined,
  EyeOutlined,
  LockOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import classNames from 'classnames';
import { IElement } from 'common/interfaces';
import { useTypedSelector } from 'hooks/useTypedSelector';
import { FC, MouseEvent, memo } from 'react';
import { useDispatch } from 'react-redux';
import CollectionService from 'services/CollectionService';
import {
  addSelectedChildElem,
  addSelectedElemId,
  groupElemsWhenChildElemTransformStop,
  setIsLayersDragDisabled,
  setSelectedChildElem,
  setSelectedElemsIds,
  toggleLayersLocking,
  toggleLayersVisibility,
  toggleNestedLayersVisibility,
} from 'store/reducers/elems.slice';

import { getLayerItemIconByElemType } from './LayerItem.helpers';
import styles from './LayerItem.module.scss';

interface IProps {
  layerData: IElement;
}

const LayerItem: FC<IProps> = ({ layerData }) => {
  const selectedElemsIds = useTypedSelector(
    (state) => state.elems.selectedElemsIds,
  );
  const elems = useTypedSelector((state) => state.elems.elems);

  const parentGroup = layerData.groupId
    ? CollectionService.findElemById(elems, layerData.groupId)
    : null;

  const dispatch = useDispatch();

  const disableLayerDrag = (): void => {
    dispatch(setIsLayersDragDisabled(true));
  };

  const enableLayerDrag = (): void => {
    dispatch(setIsLayersDragDisabled(false));
  };

  const lockHandler = (e: MouseEvent<HTMLDivElement>): void => {
    e.stopPropagation();
    dispatch(toggleLayersLocking([layerData.id]));
  };

  const visibleHandler = (e: MouseEvent<HTMLDivElement>): void => {
    e.stopPropagation();
    dispatch(toggleLayersVisibility([layerData.id]));
  };

  const toggleNestedListHandler = (e: MouseEvent<HTMLDivElement>): void => {
    e.stopPropagation();
    dispatch(toggleNestedLayersVisibility(layerData.id));
  };

  const selectOnClickHandler = (e: MouseEvent<HTMLDivElement>): void => {
    const isChildElem = Boolean(layerData.groupId);

    if (e.shiftKey) {
      if (isChildElem) {
        dispatch(addSelectedChildElem(layerData));
      } else {
        dispatch(addSelectedElemId(layerData.id));
      }
    } else {
      if (isChildElem) {
        dispatch(setSelectedChildElem(layerData));
      } else {
        dispatch(groupElemsWhenChildElemTransformStop());
        dispatch(setSelectedElemsIds([layerData.id]));
      }
    }
  };

  const isLayerActive =
    selectedElemsIds.includes(layerData.id) ||
    (parentGroup && selectedElemsIds.includes(parentGroup.id));

  const isLayerHidden = layerData.isHidden || parentGroup?.isHidden;

  return (
    <div
      className={classNames(styles.layer, {
        [styles['nested-item']]: layerData.groupId,
        [styles['active']]: isLayerActive,
        [styles['hidden']]: isLayerHidden,
      })}
      onClick={selectOnClickHandler}
    >
      {getLayerItemIconByElemType(
        layerData.elemType,
        layerData.showNestedLayers ?? false,
        toggleNestedListHandler,
      )}

      <div className={styles['name']}>{layerData.layerName}</div>

      {!parentGroup?.isBlocked ? (
        layerData.isBlocked ? (
          <LockOutlined
            onMouseDown={lockHandler}
            onMouseEnter={disableLayerDrag}
            onMouseLeave={enableLayerDrag}
          />
        ) : (
          <UnlockOutlined
            onMouseDown={lockHandler}
            onMouseEnter={disableLayerDrag}
            onMouseLeave={enableLayerDrag}
          />
        )
      ) : null}

      <div className={styles['icon-container']}>
        {!parentGroup?.isHidden ? (
          layerData.isHidden ? (
            <EyeInvisibleOutlined
              onMouseDown={visibleHandler}
              onMouseEnter={disableLayerDrag}
              onMouseLeave={enableLayerDrag}
            />
          ) : (
            <EyeOutlined
              onMouseDown={visibleHandler}
              onMouseEnter={disableLayerDrag}
              onMouseLeave={enableLayerDrag}
            />
          )
        ) : null}
      </div>
    </div>
  );
};

export default memo(LayerItem);
